import { AppFunctionComponent } from "../../../../types"
import React, { useContext } from "react"
import styled, { css } from "styled-components"
import RichText from "../../../../resolvers/rich-text/rich-text.resolver"
import { FootnoteContext } from "../../../../templates/page.template"
import {
  ContentfulImage,
  ListItemBuilder,
  ListItemLayout,
  GatsbyImage as GatsbyImageType,
} from "../../../../types"
import { RawLink } from "../../../../ui/components/link/link.styled"
import { smallBodyTextStyles } from "../../general-styles.component"
import {
  SecondaryHeading,
  tertiaryHeadingStyles,
} from "../../heading.component"
import { Image } from "../../image.component"
import { Link } from "gatsby"
import Kickstarter from "../../../../images/ks.png"

export const ItemImage = styled(Image)`
  display: block;
  max-width: calc(var(--image-width) * 1px);
  border-radius: 8px;
  width: 100%;
  img {
    object-fit: contain;
    object-position: center;
  }

  @media (max-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
    max-width: 100%;
  }
`

export const ItemIcon = styled.img`
  --icon-size: calc(var(--image-width) * 1px);

  width: var(--icon-size);
  height: var(--icon-size);
`

export const ItemText = styled.div`
  max-width: calc(var(--item-text-max-width) * 1px);

  white-space: pre-wrap;
  width: 100%;
  *:first-child {
    /* margin-top: 20px; */
  }
`
export const ItemTitle = styled(SecondaryHeading)``

export const ItemLabel = styled.span`
  line-height: 30px;
  white-space: nowrap;
  vertical-align: middle;
  ${smallBodyTextStyles};
`

export const BasicItem = styled.li<{
  withImage?: boolean
  imageWidth?: number
  background?: string
  customStyles?: ListItemBuilder["style"]
}>`
  --default-image-width: 130;
  @media (min-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
    --default-image-width: 165;
  }
  --image-width: ${({ imageWidth }) => {
    return imageWidth || `var(--default-image-width)`
  }};

  ${({ background, theme }) =>
    background &&
    css`
      background-color: ${background};
      border-radius: ${theme.size.borderRadiusSmall};
      box-sizing: border-box;
      padding-top: 15px;
      padding-bottom: 15px;
    `};

  ${({ customStyles }) =>
    customStyles &&
    css`
      --item-text-max-width: ${customStyles.text_max_width};
    `};
`

const VerticallyCenteredItem = styled(BasicItem)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  img {
    display: block;
    margin: 0 auto;
  }

  ${({ customStyles }) =>
    customStyles?.border &&
    css`
      border: ${customStyles.border};
    `}
  ${({ customStyles }) =>
    customStyles?.padding &&
    css`
      padding: ${customStyles.padding};
    `}

    ${({ customStyles }) =>
    customStyles?.border &&
    css`
      border-radius: ${customStyles.borderRadius};
    `}
`

const HorizontalItem = styled(BasicItem)`
  display: grid;
  grid-template-columns: auto 1fr;
  grid-column-gap: 40px;
`

const CenteredHorizontalForOneColumnItem = styled(BasicItem)`
  --column-gap: 40;
  --image-ratio: calc(var(--image-width) / var(--max-width));
  --column-ratio: calc(var(--column-gap) / var(--max-width));
  --content-ratio: calc(1 - var(--image-ratio) - var(--column-ratio));

  display: grid;
  grid-template-columns: calc(var(--image-ratio) * 100%) calc(
      var(--content-ratio) * 100%
    );
  grid-column-gap: calc(var(--column-gap) * 1px);

  align-items: center;
  justify-content: center;
  justify-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
    grid-template-columns: 1fr;
    grid-row-gap: calc(var(--column-gap) / 2 * 1px);
  }
`

const allHeadings = `h1, h2, h3, h4, h5, h6`

const WideItem = styled(BasicItem)`
  ${ItemText} {
    display: grid;
    ${allHeadings} {
      grid-area: Heading;
    }
    p {
      grid-area: Text;
    }
    grid-template-areas: "Heading Heading" "Text Text";
  }
`

const singleRowLayout = css`
  @media (min-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
    display: grid;
    grid-template-areas: "RowTitle RowContent" "RowTitle Image";
    grid-template-columns: 1fr 2fr;
    grid-template-rows: auto auto;
  }
`

const singleRowTitle = css`
  grid-area: RowTitle;
  max-width: 210px;
  font-weight: 500;
  * {
    font-weight: inherit;
  }
`

const SingleRowItem = styled(BasicItem)`
  ${({ withImage }) =>
    withImage
      ? css`
          display: grid;
          grid-template-areas: "RowTitle" "RowContent";
          grid-template-columns: 1fr;
          grid-template-rows: auto auto;
          ${singleRowLayout};

          ${ItemText} {
            ${singleRowTitle};
            *:first-of-type {
              margin-top: 0;
            }
          }
          ${ItemImage}, ${ItemIcon} {
            grid-area: RowContent;
          }
        `
      : css`
          ${ItemText} {
            ${allHeadings} {
              ${singleRowTitle};
              margin-top: 0;
            }
            p {
              grid-area: RowContent;
            }
            img {
              grid-area: Image;
              max-height: 100px;
              max-width: 100%;
              margin-top: 30px;
            }
            ${singleRowLayout};
          }
        `}
`

const SingleRowAlternatedItem = styled(BasicItem)`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr 1fr;
  grid-template-areas:
    "ImageContent"
    "Content";

  @media (min-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
    grid-template-areas: "Content ImageContent";
    grid-template-columns: 1fr 2fr;
    grid-template-rows: 1fr;
    grid-column-gap: 10px;
    min-height: 340px;

    &:nth-child(2n) {
      grid-template-areas: "ImageContent Content";
      grid-template-columns: 2fr 1fr;
      ${ItemImage} {
        justify-self: start;
        img {
          object-position: left;
        }
      }
      ${ItemText} {
        justify-self: end;
      }
    }
  }

  ${ItemText} {
    grid-area: Content;
    justify-self: center;
    width: 100%;
    display: flex;
    flex-direction: column;
    text-align: left;

    @media (min-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
      justify-self: start;
      align-items: flex-start;
      justify-content: center;
      max-width: 250px;
    }
  }

  ${({ withImage }) =>
    withImage &&
    css`
      ${ItemImage} {
        grid-area: ImageContent;
        justify-self: end;
        max-width: 100%;
        img {
          object-fit: contain;
          object-position: right;
        }
      }
    `}
`

const TeamItem = styled(BasicItem)`
  justify-self: center;
  text-align: center;
  ${ItemTitle} {
    margin: ${13 / 20}em 0 0;
    font-size: ${18 / 16}rem;
    line-height: 2;
    font-weight: 400;
    @media (min-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
      font-size: ${20 / 16}rem;
      line-height: 1.5;
    }
  }
  ${ItemText} {
    p {
      margin-top: 0;
      font-weight: 300;
      letter-spacing: 0;
      text-transform: initial;
      line-height: ${28 / 14}em;
      font-size: ${14 / 16}rem;
      color: ${({ theme }) => theme.color.annotation};
      font-family: ${({ theme }) => theme.font.basic};
    }
  }
  ${ItemImage} {
    max-height: 275px;
    max-width: 275px;
    border-radius: ${({ theme }) => theme.size.borderRadius};
  }
`

export const ProductItem = styled(BasicItem)`
  --item-width: ${({ imageWidth }) =>
    imageWidth ? imageWidth + "px" : "260px"};

  justify-self: center;
  ${ItemTitle} {
    margin: ${13 / 20}em 0 0;
    ${tertiaryHeadingStyles}
  }
  ${ItemText} {
    max-width: var(--item-width);
    text-align: left;
    p {
      margin-top: 0;
      font-size: ${14 / 16}rem;
      color: ${({ theme }) => theme.color.annotation};
    }

    ${RawLink} {
      ${smallBodyTextStyles};
      color: ${({ theme, customStyles }) =>
        customStyles?.linkColor ?? theme.color.link};
      margin-right: 30px;
    }

    p:last-child {
      margin-top: 15px;
    }
  }
  ${ItemImage} {
    max-height: var(--item-width);
    max-width: var(--item-width);
    border-radius: ${({ theme }) => theme.size.borderRadius};
    height: 240px;
    img {
      object-fit: cover;
    }
  }
`

const OverviewItem = styled(BasicItem)`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto;
  grid-template-areas:
    "Image"
    "Label"
    "Text";

  @media (min-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
    grid-template-columns: 380px 1fr;
    grid-template-rows: 60px auto;
    grid-column-gap: 30px;
    grid-template-areas:
      "Image Label"
      "Image Text"
      "Image Text";
  }

  @media (min-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    grid-template-columns: 460px 1fr;
    grid-column-gap: 70px;
  }

  ${ItemImage} {
    max-height: 320px;
    max-width: 320px;
    grid-area: Image;
    justify-self: center;

    @media (min-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
      max-height: 380px;
      max-width: 380px;
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
      max-height: 460px;
      max-width: 460px;
    }
  }

  ${ItemText} {
    grid-area: Text;
    text-align: left;

    ${RawLink} {
      ${smallBodyTextStyles};
      color: inherit;
      &:hover {
        color: ${({ theme }) => theme.color.link};
      }
    }
  }

  ${ItemLabel} {
    grid-area: Label;
    @media (min-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
      margin-left: 32px;
    }
  }

  ${ItemTitle} {
    grid-area: Label;
    display: flex;
    flex-direction: column;
    text-align: left;

    a {
      color: black;
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
      display: block;
    }
  }
`

const Item: AppFunctionComponent<{
  layout: ListItemLayout
  withImage?: boolean
  imageWidth?: number
  background?: string
  customStyles?: ListItemBuilder["style"]
}> = ({ layout, ...props }) => {
  switch (layout) {
    case ListItemLayout.VerticalLeftAligned:
      return <BasicItem {...props} />
    case ListItemLayout.VerticalCentered:
      return <VerticallyCenteredItem {...props} />
    case ListItemLayout.Horizontal:
      return <HorizontalItem {...props} />
    case ListItemLayout.CenteredHorizontalForOneColumn:
      return <CenteredHorizontalForOneColumnItem {...props} />
    case ListItemLayout.Wide:
      return <WideItem {...props} />
    case ListItemLayout.SingleRow:
      return <SingleRowItem {...props} imageWidth={360} />
    case ListItemLayout.SingleRowAlternated:
      return <SingleRowAlternatedItem {...props} />
    case ListItemLayout.Team:
      return <TeamItem {...props} />
    case ListItemLayout.Overview:
      return <OverviewItem {...props} />
    case ListItemLayout.ProductItem:
      return <ProductItem {...props} />
    default:
      return null
  }
}

type ImageResolverProps = (GatsbyImageType | ContentfulImage) & {
  imageWidth?: number
}

export const ItemImageResolver: AppFunctionComponent<ImageResolverProps> = (
  itemImage
) => {
  const isGatsbyImage = (
    image: ContentfulImage | GatsbyImageType
  ): image is GatsbyImageType => {
    return Boolean((image as GatsbyImageType).gatsbyImageData)
  }

  return isGatsbyImage(itemImage) ? (
    <ItemImage image={itemImage.gatsbyImageData} alt={itemImage.description} />
  ) : (
    <ItemIcon src={itemImage.file.url} alt={itemImage.description} />
  )
}

interface Props extends ListItemBuilder {
  layout: ListItemLayout
  imageWidth?: number
  background?: string
}

const KickstarterLabel = ({ url }: { url: string }) => {
  return (
    <span>
      Now on{" "}
      <Link to={url}>
        <img
          style={{ position: "relative" }}
          width={180}
          height={20}
          src={Kickstarter}
          alt="Kickstarter"
        />
      </Link>
    </span>
  )
}

const ResolveLabel = ({ label }: { label: string }) => {
  if (label.includes("kickstarter.com")) {
    return <KickstarterLabel url={label} />
  }
  return <span>{label}</span>
}

const ListItem: AppFunctionComponent<Props> = ({
  className,
  layout,
  imageWidth,
  background,
  style,
  ...item
}) => {
  const footnotesOptions = useContext(FootnoteContext)

  return (
    <Item
      layout={layout}
      withImage={Boolean(item.image)}
      imageWidth={imageWidth}
      background={background}
      className={className}
      customStyles={style}
    >
      {item.image &&
        (item.link ? (
          <Link style={{ gridArea: "Image" }} to={item.link} target="_blank">
            <ItemImageResolver {...item.image} />
          </Link>
        ) : (
          <ItemImageResolver {...item.image} />
        ))}
      {item.title && (
        <ItemTitle>
          {item.link ? <Link to={item.link}>{item.title}</Link> : item.title}

          {item.label && (
            <ItemLabel>
              <ResolveLabel label={item.label} />
            </ItemLabel>
          )}
        </ItemTitle>
      )}
      <ItemText>
        {item.text && (
          <RichText content={item.text} options={footnotesOptions} />
        )}
      </ItemText>
    </Item>
  )
}

export default ListItem
